import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a Tailwind color string like "emerald" or "emerald-500"
 * Returns the base color name and shade number
 */
export function parseColor(color: string): { base: string; shade: number } {
  const match = color.match(/^([a-z]+)(?:-(\d+))?$/);
  if (!match) return { base: "gray", shade: 500 };
  return { base: match[1], shade: match[2] ? parseInt(match[2], 10) : 500 };
}

/**
 * Get the next shade level (for hover states)
 * 500 -> 600, 600 -> 700, 900 -> 950, 950 -> 900
 */
function getHoverShade(shade: number): number {
  if (shade >= 950) return 900;
  if (shade >= 900) return 950;
  return Math.min(shade + 100, 900);
}

/**
 * Get a lighter shade (for borders, rings)
 * 500 -> 400, 400 -> 300, 100 -> 50
 */
function getLighterShade(shade: number): number {
  if (shade <= 50) return 50;
  if (shade <= 100) return 50;
  return shade - 100;
}

/**
 * Generate Tailwind color classes for button styling
 */
export function getButtonColorClasses(color: string) {
  const { base, shade } = parseColor(color);
  const hoverShade = getHoverShade(shade);
  return {
    default: `bg-${base}-${shade} hover:bg-${base}-${hoverShade}`,
    link: `text-${base}-${shade}`,
  };
}

/**
 * Generate Tailwind color classes for checkbox styling
 */
export function getCheckboxColorClasses(color: string) {
  const { base, shade } = parseColor(color);
  const borderShade = getLighterShade(shade);
  return {
    checked: `data-[state=checked]:bg-${base}-${shade} data-[state=checked]:border-${base}-${shade}`,
    border: `border-${base}-${borderShade}`,
    ring: `focus-visible:ring-${base}-${shade}`,
  };
}

/**
 * Generate Tailwind color classes for switch/toggle styling
 */
export function getSwitchColorClasses(color: string) {
  const { base, shade } = parseColor(color);
  return {
    checked: `data-[state=checked]:bg-${base}-${shade}`,
    ring: `focus-visible:ring-${base}-${shade}`,
  };
}

/**
 * Generate Tailwind color classes for progress bar styling
 */
export function getProgressColorClasses(color: string) {
  const { base, shade } = parseColor(color);
  return `bg-${base}-${shade}`;
}

/**
 * Generate Tailwind color classes for badge styling
 */
export function getBadgeColorClasses(
  color: string,
  style: "solid" | "subtle" = "subtle",
) {
  const { base, shade } = parseColor(color);
  const hoverShade = getHoverShade(shade);
  if (style === "solid") {
    return `bg-${base}-${shade} text-white hover:bg-${base}-${hoverShade}`;
  }
  // subtle: lighter bg, darker text
  const bgShade = Math.min(100, shade - 400);
  const textShade = Math.max(800, shade + 300);
  return `bg-${base}-${bgShade > 0 ? bgShade : 100} text-${base}-${textShade > 900 ? 800 : textShade}`;
}

/**
 * Generate Tailwind color classes for slider styling
 */
export function getSliderColorClasses(color: string) {
  const { base, shade } = parseColor(color);
  return {
    range: `bg-${base}-${shade}`,
    thumb: `border-${base}-${shade} focus-visible:ring-${base}-${shade}`,
  };
}
